import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../config/supabase';
import { EmployeeStatus } from '../types';

export const useEmployeeStatus = () => {
  const [employeeStatuses, setEmployeeStatuses] = useState<EmployeeStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Función para obtener estados de empleados
  const fetchEmployeeStatuses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Obtener empleados con sus configuraciones y última actividad
      const { data: employees, error: employeesError } = await supabase
        .from('profiles')
        .select(`
          *,
          alert_settings (*)
        `)
        .eq('role', 'employee');

      if (employeesError) throw employeesError;

      // Procesar los datos
      const statusesWithSettings = employees.map(employee => {
        const settings = employee.alert_settings?.[0];
        const lastActivity = new Date(employee.last_activity || 0);
        const now = new Date();
        const diffMinutes = Math.floor((now.getTime() - lastActivity.getTime()) / 60000);
        
        // Un empleado está online si su última actividad está dentro del intervalo configurado
        const intervalMinutes = settings?.interval_minutes || 1;
        const isOnline = diffMinutes < intervalMinutes;

        // Si el empleado está marcado como activo pero debería estar inactivo
        if (!isOnline && employee.is_active) {
          console.log('Empleado inactivo detectado, actualizando estado:', employee.id);
          supabase
            .from('profiles')
            .update({
              is_active: false,
              has_webcam: false,
              webcam_last_checked: new Date().toISOString()
              // No actualizamos last_activity aquí
            })
            .eq('id', employee.id)
            .then(({ error }) => {
              if (error) {
                console.error('Error al actualizar estado de inactividad:', error);
              }
            });
        }

        return {
          ...employee,
          isOnline,
          work_start_time: settings?.work_start_time,
          work_end_time: settings?.work_end_time,
          interval_minutes: settings?.interval_minutes
        };
      });

      setEmployeeStatuses(statusesWithSettings);
      setLoading(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
      setLoading(false);
    }
  }, []);

  // Suscribirse a cambios en profiles
  useEffect(() => {
    // Canal para profiles
    const profilesChannel = supabase
      .channel('profile-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: 'role=eq.employee'
        },
        () => {
          fetchEmployeeStatuses();
        }
      )
      .subscribe();

    // Canal para employee_status
    const statusChannel = supabase
      .channel('employee-status-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'employee_status'
        },
        () => {
          fetchEmployeeStatuses();
        }
      )
      .subscribe();

    // Cargar estados iniciales
    fetchEmployeeStatuses();

    // Actualizar cada 5 segundos para mantener el estado al día
    const intervalId = setInterval(fetchEmployeeStatuses, 5000);

    return () => {
      profilesChannel.unsubscribe();
      statusChannel.unsubscribe();
      clearInterval(intervalId);
    };
  }, [fetchEmployeeStatuses]);

  return { employeeStatuses, loading, error };
};
